import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { SERVER_ERROR } from "constants/errors"
import { CLOSE_FRIENDS, ITEM, OWNER } from "constants/index"
import {
  CLOSE_CREATE_NEW_LIST_OVERLAY,
  FILTER_NOTIFICATIONS,
  NAVIGATE_TO_ITEM_PAGE,
  NAVIGATE_TO_OTHER_USER_PROFILE_PAGE,
  SIGN_OUT_USER
} from "constants/events"
import { WishamNotification } from "types/notification.types"
import { isItemViewPage, PATH_INFOS } from "utils/navigatorUtils"
import useEventListener from "hooks/useEventListener"
import { AppDispatch } from "appRedux/store"
import { useAppSelector } from "appRedux/hooks"
import { resetItemFormSlice, updateShowCreateNewListOverlay } from "appRedux/slices/itemFormSlice"
import { resetWishListSlice, updateShowCommentsBottomSheet, updateShowGeniesList, updateShowLikedByBottomSheet } from "appRedux/slices/wishListSlice"
import { resetListFormSlice } from "appRedux/slices/listFormSlice"
import { updateSearchedUserIsLoggedInUser } from "appRedux/slices/globalSlice"
import { updateFollowRequests } from "appRedux/slices/userSlice"
import { updateNotifications } from "appRedux/slices/notificationSlice"
import { useGetNotificationsCountQuery } from "appRedux/slices/notificationApiSlice"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useInitQuery } from "appRedux/slices/initApiSlice"
import { isPublicPage } from "appRedux/baseQuery"
import { useAuth } from "context/AuthContext"
import { PremiumAccessProvider } from "context/PremiumAccessProvider"
import Toast from "components/atoms/Toast"
import PageTitle from "components/atoms/PageTitle"
import Navigation from "components/molecules/Navigation"
import PremiumOnboarding from "components/templates/PremiumOnboarding"
import FadeInAnimation from "components/animations/FadeInAnimation"

const GET_UNSEEN_NOTIFICATIONS_COUNT_INTERVAL = 30 * 1000

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const globalToastMessage = useAppSelector((state) => state.global.message)
  const role = useAppSelector((state) => state.global.role)
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const previousPageInfo = useAppSelector((state) => state.global.previousPageInfo)
  const previousPath = useAppSelector((state) => state.global.previousPath)
  const notifications = useAppSelector((state) => state.notification.notifications)
  const followRequests = useAppSelector((state) => state.user.followRequests)
  const wishListViewMode = useAppSelector((state) => state.wishList.viewMode)
  const listForm = useAppSelector((state) => state.listForm)

  const { logout, isAuthenticated, user } = useAuth()

  useInitQuery(undefined, { skip: window.location.pathname.startsWith("/public") })
  useGetNotificationsCountQuery(undefined, {
    pollingInterval: GET_UNSEEN_NOTIFICATIONS_COUNT_INTERVAL,
    skip: !isAuthenticated || currentPageInfo?.path?.startsWith("/notifications")
  })

  const dispatch = useDispatch() as AppDispatch
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEventListener(document, SIGN_OUT_USER, logoutUser)

  useEventListener(document, NAVIGATE_TO_OTHER_USER_PROFILE_PAGE, handleNavigateToOtherUserProfilePage)

  useEventListener(document, FILTER_NOTIFICATIONS, filterNotification)

  useEventListener(document, CLOSE_CREATE_NEW_LIST_OVERLAY, handleCloseCreateNewListOverlay)

  useEventListener(document, NAVIGATE_TO_ITEM_PAGE, handleNavigateToItemPage)

  useEffect(() => {
    if (!previousPath) return
    handleReduxStates(previousPageInfo.path)
  }, [previousPageInfo?.path])

  useEffect(() => {
    const isOwner = getSearchedUserIsLoggedInUser(currentPageInfo?.dynamicPathParts?.handle, user?.handle, currentPageInfo?.path)
    dispatch(updateSearchedUserIsLoggedInUser(isOwner))
  }, [user?.handle, currentPageInfo?.path, currentPageInfo?.dynamicPathParts])

  async function logoutUser() {
    try {
      await logout()
    } catch (err) {
      console.error("Error happened in logoutUser method. Error:", err)
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: SERVER_ERROR,
        show: true,
        status: 500,
        message: `general.${SERVER_ERROR}`
      }))
    }
  }

  function handleNavigateToOtherUserProfilePage(event: CustomEvent) {
    const eventDetail: { id: number, handle: string } = event.detail
    const handle = eventDetail.handle
    navigate(`/user/${handle}`)
  }

  function filterNotification(event: CustomEvent) {
    const detail = event.detail
    const filteredFollowRequests = followRequests.filter((notif: FollowRequest) => notif.followerId !== detail.followerId)
    dispatch(updateFollowRequests(filteredFollowRequests))
    if (detail.type === "ALL") {
      const filteredNotifications = notifications.filter((notif: WishamNotification) => notif.id !== detail.notificationId)
      dispatch(updateNotifications(filteredNotifications))
    }
  }

  function handleCloseCreateNewListOverlay() {
    dispatch(updateShowCreateNewListOverlay(false))
  }

  function handleNavigateToItemPage(event: CustomEvent) {
    const eventDetail = event.detail
    if (!eventDetail) return
    navigate(`/user/${eventDetail.handle}/item/${eventDetail.itemId}`)
  }

  function skipResettingListFormSlice() {
    return !(listForm.formFields.privacy === CLOSE_FRIENDS && currentPageInfo.path === PATH_INFOS.closeFriends.path)
  }

  function skipResettingWishListSlice() {
    return (currentPageInfo.path !== PATH_INFOS.editItem.path)
  }

  function handleReduxStates(path: string) {
    if (!path) return
    switch (path) {
      case PATH_INFOS.item.path:
      case PATH_INFOS.editItem.path:
        dispatch(resetItemFormSlice())
        break
      case PATH_INFOS.list.path && skipResettingListFormSlice():
      case PATH_INFOS.editList.path && skipResettingListFormSlice():
        dispatch(resetListFormSlice())
        break
      case PATH_INFOS.viewItem.path && skipResettingWishListSlice():
      case PATH_INFOS.viewItems.path && skipResettingWishListSlice():
        dispatch(resetWishListSlice())
      case PATH_INFOS.viewItem.path:
      case PATH_INFOS.viewList.path:
      case PATH_INFOS.viewItems.path:
        dispatch(updateShowCommentsBottomSheet(false))
        dispatch(updateShowLikedByBottomSheet(false))
        dispatch(updateShowGeniesList(false))
        break
    }
  }

  function getSearchedUserIsLoggedInUser(dynamicHandleFromPath?: string, loggedInUserHandle?: string, currentPath?: string) {
    if (!loggedInUserHandle || !currentPath) return null
    if (currentPath?.includes("/:handle")) {
      return dynamicHandleFromPath === loggedInUserHandle
    } else {
      return true
    }
  }

  function shouldRenderNavigation() {
    return (currentPageInfo?.showInNavigation || false) && isAuthenticated && role === OWNER
  }

  function getMinHeight() {
    let offset = 0
    if (currentPageInfo?.title || currentPageInfo?.hasBackButton || currentPageInfo?.headerButtons) offset += 48
    if ((isItemViewPage(currentPageInfo?.path) && wishListViewMode === ITEM) || (currentPageInfo?.path === PATH_INFOS.profile.path)) offset += 48
    if (shouldRenderNavigation()) offset += 60
    const viewportUnit = CSS.supports("height: 100dvh") ? "100dvh" : "100vh"

    return `calc(${viewportUnit} - ${offset}px)`
  }

  function getMargin() {
    let topMargin = currentPageInfo?.showInNavigation ? 0 : 48
    let bottomMargin = 0
    if (currentPageInfo?.title) topMargin += 0
    if (shouldRenderNavigation()) bottomMargin += 60
    return `${topMargin}px 0 ${bottomMargin}px 0`
  }

  function getPadding() {
    return (currentPageInfo?.useLayoutWithPadding || false) ? "8px 16px 30px" : "0"
  }

  function shouldRenderFullLayout() {
    return isAuthenticated || isPublicPage()
  }

  return (
    <FadeInAnimation key={currentPageInfo?.key}>
      <PremiumAccessProvider>
        <PageContainer id="main-layout">
          {shouldRenderFullLayout() && <>
              <PageTitle/>
              <PageContent style={{
                margin: getMargin(),
                padding: getPadding(),
                minHeight: getMinHeight()
              }}>
                {children}
                {globalToastMessage && (<Toast
                  key={globalToastMessage.key}
                  open={globalToastMessage.show}
                  severity={globalToastMessage.severity}
                  message={t(globalToastMessage.message)}
                />)}
              </PageContent>
            {shouldRenderNavigation() && <Navigation/>}
          </>}
          {!shouldRenderFullLayout() && <>
            {children}
          </>}
        </PageContainer>
      </PremiumAccessProvider>
      <PremiumOnboarding/>
    </FadeInAnimation>
  )
}

const PageContainer = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  overflow: "hidden",
  backgroundColor: theme.palette.gray["2"],
  alignItems: "center",
  position: "relative"
}))

const PageContent = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  minHeight: "100vh",
  position: "relative",

  "@supports (height: 100dvh)": {
    minHeight: "calc(100dvh)"
  }
}))
